/**
 * Master Bot - Referrals Page
 * Referral system tracking (3-10 referrals/month, $5 reward)
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Button from '../../components/Button';
import StatsCard from '../../components/StatsCard';
import { useTelegram } from '../../hooks/useTelegram';

const ReferralLink = styled(Card)`
  background: var(--bg-card);
  margin-bottom: var(--spacing-lg);
`;

const LinkLabel = styled.div`
  font-size: var(--font-sm);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-sm);
`;

const LinkBox = styled.div`
  display: flex;
  gap: var(--spacing-md);
  align-items: center;
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--radius-md);
  margin-bottom: var(--spacing-md);
`;

const Link = styled.div`
  flex: 1;
  font-size: var(--font-sm);
  color: var(--accent-green);
  font-family: monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CopyButton = styled(Button)`
  flex-shrink: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
`;

const InfoCard = styled(Card)`
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid var(--accent-green);
`;

const InfoTitle = styled.h3`
  font-size: var(--font-md);
  font-weight: var(--font-semibold);
  color: var(--accent-green);
  margin-bottom: var(--spacing-md);
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const InfoItem = styled.li`
  font-size: var(--font-sm);
  color: var(--text-secondary);
  padding: var(--spacing-xs) 0;
  padding-left: var(--spacing-md);
  position: relative;

  &:before {
    content: '•';
    position: absolute;
    left: 0;
    color: var(--accent-green);
  }
`;

const SectionTitle = styled.h3`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
  margin: var(--spacing-lg) 0 var(--spacing-md);
`;

const ReferralList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const ReferralCard = styled(Card)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ReferralInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const ReferralName = styled.div`
  font-size: var(--font-md);
  font-weight: var(--font-medium);
  color: var(--text-primary);
`;

const ReferralDate = styled.div`
  font-size: var(--font-sm);
  color: var(--text-secondary);
`;

const ReferralStatus = styled.span<{ status: string }>`
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--radius-md);
  font-size: var(--font-sm);
  font-weight: var(--font-medium);
  background: ${({ status }) => {
    switch (status) {
      case 'active':
        return 'rgba(76, 175, 80, 0.2)';
      case 'pending':
        return 'rgba(255, 193, 7, 0.2)';
      default:
        return 'rgba(142, 142, 147, 0.2)';
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'active':
        return 'var(--accent-green)';
      case 'pending':
        return '#FFC107';
      default:
        return 'var(--text-secondary)';
    }
  }};
`;

const MasterReferrals: React.FC = () => {
  const navigate = useNavigate();
  const { showBackButton, notificationFeedback } = useTelegram();
  const [referralLink] = useState('https://t.me/masterbot?start=ref_user123');
  const [stats] = useState({
    thisMonth: 2,
    total: 15,
    earnings: 25.0,
  });
  const [referrals] = useState([
    { id: 1, name: 'John Doe', date: 'Oct 5, 2025', status: 'active' },
    { id: 2, name: 'Jane Smith', date: 'Oct 3, 2025', status: 'pending' },
    { id: 3, name: 'Bob Wilson', date: 'Sep 28, 2025', status: 'active' },
  ]);

  useEffect(() => {
    showBackButton(() => navigate('/master/dashboard'));
  }, [showBackButton, navigate]);

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    notificationFeedback('success');
  };

  const shareReferralLink = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Join Bot Builder Platform',
        text: 'Create your own Telegram e-commerce bot!',
        url: referralLink,
      });
    }
  };

  return (
    <Layout header={<Navbar title="Referrals" subtitle="Earn $5 per month" showBack />}>
      <ReferralLink>
        <LinkLabel>Your Referral Link</LinkLabel>
        <LinkBox>
          <Link>{referralLink}</Link>
          <CopyButton size="small" onClick={copyReferralLink}>
            📋 Copy
          </CopyButton>
        </LinkBox>
        <Button fullWidth variant="secondary" onClick={shareReferralLink}>
          📤 Share Link
        </Button>
      </ReferralLink>

      <StatsGrid>
        <StatsCard label="This Month" value={stats.thisMonth} icon="👥" color="var(--accent-green)" />
        <StatsCard label="Total Referrals" value={stats.total} icon="🎯" />
        <StatsCard
          label="Total Earnings"
          value={`$${stats.earnings.toFixed(2)}`}
          icon="💰"
          color="var(--accent-green)"
        />
        <StatsCard
          label="Next Reward"
          value={`${3 - stats.thisMonth} more`}
          icon="🎁"
          color={stats.thisMonth >= 3 ? 'var(--accent-green)' : 'var(--text-secondary)'}
        />
      </StatsGrid>

      <InfoCard>
        <InfoTitle>💡 How It Works</InfoTitle>
        <InfoList>
          <InfoItem>Refer 3-10 vendors per month</InfoItem>
          <InfoItem>Each must subscribe for 1 full month</InfoItem>
          <InfoItem>Earn $5 reward when 3+ qualify</InfoItem>
          <InfoItem>Less than 3? Get $0.10 per referral</InfoItem>
          <InfoItem>Rewards reset monthly</InfoItem>
        </InfoList>
      </InfoCard>

      <SectionTitle>Your Referrals ({referrals.length})</SectionTitle>
      <ReferralList>
        {referrals.map((referral) => (
          <ReferralCard key={referral.id}>
            <ReferralInfo>
              <ReferralName>{referral.name}</ReferralName>
              <ReferralDate>Joined {referral.date}</ReferralDate>
            </ReferralInfo>
            <ReferralStatus status={referral.status}>
              {referral.status === 'active' ? 'Active' : 'Pending'}
            </ReferralStatus>
          </ReferralCard>
        ))}
      </ReferralList>
    </Layout>
  );
};

export default MasterReferrals;
